"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, ArrowRight, FileText, CircleAlert, Send, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function formatDate(dateString?: string) {
  if (!dateString) return "Terbaru";
  try {
    const date = new Date(dateString);
    const datePart = new Intl.DateTimeFormat("id-ID", {
      month: "short",
      day: "numeric",
      year: "numeric"
    }).format(date);
    const timePart = new Intl.DateTimeFormat("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    }).format(date);
    return `${datePart} | ${timePart} WIB`;
  } catch (e) {
    return "Terbaru";
  }
}
function UpdatesList() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div data-section-id="94957" className="flex flex-col">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="border-b border-border/50 py-6 flex items-center gap-6 animate-pulse">
            <div className="h-8 w-28 bg-muted rounded shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-5 w-3/4 bg-muted rounded" />
              <div className="h-4 w-1/2 bg-muted rounded" />
            </div>
            <div className="h-4 w-32 bg-muted rounded shrink-0 hidden md:block" />
          </div>)}
      </div>;
  }
  if (error) {
    return <Card className="border-destructive/10 bg-destructive/5 p-6 text-center flex flex-col items-center justify-center gap-3">
        <CircleAlert className="size-8 text-destructive/60" />
        <p className="text-sm text-destructive/70 font-medium">Gagal memuat data klarifikasi terbaru.</p>
      </Card>;
  }
  if (!posts || posts.length === 0) {
    return <Card className="border-border/40 bg-muted/10 backdrop-blur-sm p-8 text-center flex flex-col items-center justify-center gap-3">
        <FileText className="size-10 text-muted-foreground/30" />
        <p className="text-muted-foreground font-medium">Belum ada pembaruan klarifikasi saat ini.</p>
      </Card>;
  }
  return <div className="flex flex-col">
      {posts.map((post, i) => {
      const categoryName = post.categories?.[0]?.name || "Klarifikasi";
      return <Link key={post.id} to={post.link || "#"} data-index={i} className="block group outline-none focus-visible:ring-2 focus-visible:ring-primary">
            <Card className="bg-transparent border-none border-b border-border/50 last:border-0 py-5 px-0 gap-0 transition-colors hover:bg-accent/30">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
                
                <div className="shrink-0">
                  <Badge variant="outline" className="text-primary border-primary/30 bg-white font-bold px-3 py-1.5 uppercase tracking-wider text-[10px] md:text-xs min-w-[120px] justify-center">
                    {categoryName}
                  </Badge>
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-black text-base md:text-lg leading-tight line-clamp-2 group-hover:text-primary transition-colors duration-300">
                    {post.title}
                  </h3>
                  {post.excerpt && <p className="text-muted-foreground text-sm mt-1 line-clamp-1">
                      {post.excerpt.replace(/<[^>]*>/g, '')}
                    </p>}
                </div>

                <div className="shrink-0 flex items-center gap-2 text-xs font-medium text-muted-foreground md:min-w-[160px] justify-end">
                  <SafeHydrate><Calendar className="size-3.5 text-muted-foreground/60" /></SafeHydrate>
                  <time dateTime={post.date}>{formatDate(post.date)}</time>
                </div>

              </div>
            </Card>
          </Link>;
    })}
    </div>;
}
export default function LatestProgramUpdates() {
  const wp_query_params = {
    per_page: 4,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="klarifikasi-terbaru" className="bg-white py-12 md:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          
          <div className="lg:col-span-2 flex flex-col gap-10">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="h-1 w-10 bg-primary rounded-lg" />
                <span className="text-primary font-bold text-sm uppercase tracking-widest">Update Terbaru</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tighter text-black">
                Ruang Klarifikasi
              </h2>
              <p className="text-muted-foreground text-base md:text-lg max-w-3xl leading-relaxed">
                Komitmen editorial ABADI.NEWS dalam menyajikan fakta akurat, hak jawab, dan verifikasi informasi yang transparan bagi publik.
              </p>
            </div>

            <PostsProvider wp_query={{
            per_page: 4,
            orderby: "date",
            order: "desc"
          }}>
              <UpdatesList />
            </PostsProvider>

            <div className="mt-2 flex justify-center">
              <Link to="/ClarificationPage">
                <Button variant="link" className="group text-primary font-bold text-base p-0 h-auto hover:no-underline">
                  Lihat Semua Klarifikasi
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-1">
            <Card className="lg:sticky lg:top-24 bg-orange-50/80 border-orange-100 shadow-sm rounded-lg overflow-hidden py-8">
              <CardHeader className="pb-4 pt-0">
                <CardTitle className="text-2xl md:text-3xl font-bold text-secondary leading-tight">
                  Ajukan Klarifikasi / Hak Jawab
                </CardTitle>
                <CardDescription className="text-secondary/80 text-sm md:text-base leading-relaxed mt-4">
                  Jika Anda merasa dirugikan oleh suatu pemberitaan atau memiliki informasi yang perlu diluruskan, sampaikan klarifikasi atau hak jawab Anda secara resmi kepada kami.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <Link to="/SendNewsPage">
                  <Button className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-7 text-lg rounded-md shadow-md transition-all duration-300">
                    <Send className="mr-2 size-5" />
                    Ajukan Sekarang
                  </Button>
                </Link>
              </CardContent>
              <CardFooter className="flex flex-col items-start gap-3 border-t border-orange-200/50 pt-6 mt-6 pb-0">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="size-5 text-secondary/40 shrink-0 mt-0.5" />
                  <p className="text-xs text-secondary/70 leading-relaxed">
                    Semua pengajuan akan diverifikasi dan diproses sesuai ketentuan UU Pers No. 40 Tahun 1999.
                  </p>
                </div>
              </CardFooter>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}